import type { ApolloError } from '@apollo/client';

export const CLASSIFIER_UNCLASSIFIABLE_CODE = 'CLASSIFIER_UNCLASSIFIABLE';
export const CLASSIFIER_UNAVAILABLE_CODE = 'CLASSIFIER_UNAVAILABLE';

/**
 * What to tell the reader when classifying their study description failed, so they know whether to reword it or retry.
 *
 * Branches on the `extensions.code` the server sets, never on message text: outside local the server replaces every
 * message with "Internal server error.", so matching the classifier's wording only ever worked on a developer's machine.
 */
export const getStudyClassificationErrorMessage = (error: ApolloError): string => {
  const codes = error.graphQLErrors.map((e) => e.extensions?.code);

  if (codes.includes(CLASSIFIER_UNCLASSIFIABLE_CODE)) {
    return 'Could not classify that; try describing the study differently.';
  }
  if (codes.includes(CLASSIFIER_UNAVAILABLE_CODE)) {
    return 'Study classification is temporarily unavailable. Try again in a moment.';
  }
  // The request never got an HTTP response, so the description is not what failed. A ServerError/ServerParseError
  // carries a statusCode: the server was reached (e.g. a gateway 502), so "check your connection" would mislead.
  if (error.networkError && !('statusCode' in error.networkError)) {
    return "Can't reach the server. Check your connection and try again.";
  }
  return 'Failed to get study classification. Contact support if this persists.';
};
